/**
 * The cat's character controller.
 *
 * A Rapier KINEMATIC character controller, not a dynamic rigidbody. Kinematic
 * means we compute the movement we want and Rapier tells us how much of it is
 * legal - precise, predictable, and it never fights the solver. A dynamic body
 * would give "realistic" physics and terrible control, which is the wrong trade
 * for a game about precisely knocking one specific vase off one specific table.
 *
 * Ragdoll is a SEPARATE body (see ragdoll.ts, M2). Both always exist; only one
 * is active at a time. Never try to make one body do both jobs.
 *
 * Every number here comes from tuning.cat. If you want to change how the cat
 * feels, edit data/tuning.json - not this file.
 */

import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { InputFrame } from '@/engine/input';
import type { Vec3 } from '@/core/math';
import { clamp, clone, DEG_TO_RAD, lengthOf, normalize, rotateToward, vec3 } from '@/core/math';
import { tuning } from '@/core/tuning';
import { warnOnce } from '@/core/assert';
import type { PhysicsWorld } from '@/engine/physicsWorld';
import {
  createAssetObject,
  createPlaceholderObject,
  getAssetDef,
  isPlaceholder,
} from '@/engine/assetRegistry';
import { addOutline, addOutlinesRecursive } from '@/render/outline';
import { makeToonMaterial } from '@/render/toonMaterial';
import {
  ProceduralAnimationDriver,
  type AnimationDriver,
  type LegRigBinding,
  type ProceduralRigBindings,
} from '@/cat/animationDriver';
import { bindImportedQuadrupedRig } from '@/cat/importedRig';
import {
  createSkeletonDescription,
  QuadrupedLocomotion,
  type LegName,
  type SkeletonDescription,
} from '@/cat/quadrupedLocomotion';
import { CatRagdoll, type RagdollReason } from '@/cat/ragdoll';

export type CatState = 'grounded' | 'airborne' | 'dashing' | 'pouncing' | 'tumbling' | 'caught';

export interface CatNoiseOutput {
  loudness: number;
  tag: string;
}

const VIEW_UP = new THREE.Vector3(0, 1, 0);

export class CatController {
  readonly position: Vec3;
  readonly velocity: Vec3 = vec3();
  /** Facing direction, radians. */
  yaw = 0;

  state: CatState = 'grounded';
  grounded = true;
  crouching = false;

  private body: RAPIER.RigidBody;
  private collider: RAPIER.Collider;
  private controller: RAPIER.KinematicCharacterController;

  private dashTimer = 0;
  private dashCooldown = 0;
  private dashDirection: Vec3 = vec3();

  private pounceCharge = 0;
  private pounceCharging = false;

  private coyoteTimer = 0;

  /** Noise produced this step, consumed by the caller and emitted on the bus. */
  noise: CatNoiseOutput = { loudness: 0, tag: 'silence' };

  /** Set on any touchdown this step. Drives the ordinary landing thump. */
  landedThisStep = false;

  /** Set when the landing was hard enough to ragdoll, for VFX and camera shake. */
  landedHardThisStep = false;
  landingImpactSpeed = 0;

  /** True while the cat is being carried back to bed after a catch. */
  private locked = false;

  /** The cat's visual. Actors own their view; SYSTEMS stay pure. See AGENTS.md. */
  readonly object: THREE.Object3D;
  private readonly locomotion: QuadrupedLocomotion;
  private readonly animationDriver: AnimationDriver | null;
  private readonly ragdoll: CatRagdoll;
  private recoveryBlend: {
    position: THREE.Vector3;
    rotation: THREE.Quaternion;
    elapsed: number;
  } | null = null;
  private readonly viewTargetPosition = new THREE.Vector3();
  private readonly viewTargetRotation = new THREE.Quaternion();
  private headLookTarget: Vec3 | undefined;

  constructor(physics: PhysicsWorld, spawn: Vec3, scene: THREE.Scene) {
    this.position = clone(spawn);
    const created = physics.addCharacterCapsule({
      position: spawn,
      radius: tuning.cat.radius,
      height: tuning.cat.height,
      isCat: true,
    });
    this.body = created.body;
    this.collider = created.collider;
    this.controller = physics.createCharacterController(0.02);

    this.object = new THREE.Object3D();
    this.object.name = 'cat';
    let body = createAssetObject('char.cat');
    let usesPlaceholder = isPlaceholder('char.cat');
    let importedBindings: ProceduralRigBindings | null = null;
    if (!usesPlaceholder) {
      const rig = getAssetDef('char.cat')?.rig;
      if (rig?.kind === 'quadruped') {
        const result = bindImportedQuadrupedRig(body, rig);
        importedBindings = result.bindings;
        if (result.problems.length > 0) {
          warnOnce(
            'cat:invalid-imported-rig',
            `Imported cat rig was rejected: ${result.problems.join('; ')}. Using placeholder.`,
          );
        }
      } else {
        warnOnce(
          'cat:missing-imported-rig',
          'Imported cat has no quadruped rig contract. Using placeholder.',
        );
      }

      if (!importedBindings) {
        body = createPlaceholderObject('char.cat');
        usesPlaceholder = true;
      }
    }
    addOutlinesRecursive(body, { outlineClass: 'character' });
    const skeleton = createSkeletonDescription();
    this.locomotion = new QuadrupedLocomotion(skeleton, physics);
    this.ragdoll = new CatRagdoll(physics, skeleton);

    // Both visuals plug into one pose boundary. Rejecting an incomplete imported
    // rig here prevents a superficially successful art swap from erasing M2.
    if (usesPlaceholder) {
      const placeholder = buildPlaceholderRig(body, skeleton);
      this.object.add(placeholder.root);
      this.animationDriver = new ProceduralAnimationDriver(placeholder.bindings);
    } else if (importedBindings) {
      this.object.add(body);
      this.animationDriver = new ProceduralAnimationDriver(importedBindings);
    } else {
      throw new Error('cat visual has neither a placeholder nor validated imported bindings');
    }

    scene.add(this.object);
  }

  /** Syncs the visual to the simulated transform. Called from the render frame. */
  updateView(realDt = 0): void {
    if (this.ragdoll.active) {
      const pose = this.ragdoll.visualPose();
      this.object.position.set(pose.objectPosition.x, pose.objectPosition.y, pose.objectPosition.z);
      this.object.quaternion.set(
        pose.objectRotation.x,
        pose.objectRotation.y,
        pose.objectRotation.z,
        pose.objectRotation.w,
      );
      this.animationDriver?.applyRagdollPose?.(pose);
      return;
    }

    const pose = this.locomotion.update({
      dt: realDt,
      position: this.position,
      velocity: this.velocity,
      yaw: this.yaw,
      grounded: this.grounded,
      ...(this.headLookTarget ? { headLookTarget: this.headLookTarget } : {}),
    });
    this.animationDriver?.applyPose(pose);

    this.viewTargetPosition.set(this.position.x, this.position.y, this.position.z);
    this.viewTargetRotation.setFromAxisAngle(VIEW_UP, this.yaw);
    if (!this.recoveryBlend) {
      this.object.position.copy(this.viewTargetPosition);
      this.object.quaternion.copy(this.viewTargetRotation);
      return;
    }

    const duration = tuning.cat.ragdoll.recoveryBlendSeconds;
    this.recoveryBlend.elapsed = Math.min(duration, this.recoveryBlend.elapsed + realDt);
    const amount = duration > 0 ? this.recoveryBlend.elapsed / duration : 1;
    this.object.position.lerpVectors(this.recoveryBlend.position, this.viewTargetPosition, amount);
    this.object.quaternion.slerpQuaternions(
      this.recoveryBlend.rotation,
      this.viewTargetRotation,
      amount,
    );
    if (this.recoveryBlend.elapsed >= duration) this.recoveryBlend = null;
  }

  /** Optional presentation target; null restores movement-direction look. */
  lookAt(target: Vec3 | null): void {
    this.headLookTarget = target ? clone(target) : undefined;
  }

  get isDashing(): boolean {
    return this.dashTimer > 0;
  }

  get dashCooldownRemaining(): number {
    return Math.max(0, this.dashCooldown);
  }

  /** 0-1 charge on the pounce. Drives the HUD charge arc. */
  get pounceChargeRatio(): number {
    const { minChargeTime, maxChargeTime } = tuning.cat.pounce;
    if (!this.pounceCharging) return 0;
    return clamp((this.pounceCharge - minChargeTime) / (maxChargeTime - minChargeTime), 0, 1);
  }

  get isRagdollActive(): boolean {
    return this.ragdoll.active;
  }

  get isControllerActive(): boolean {
    return this.body.isEnabled();
  }

  /** Head position, used for the camera focus and granny's LOS target. */
  get headPosition(): Vec3 {
    if (this.ragdoll.active) return this.ragdoll.headPosition;
    return vec3(this.position.x, this.position.y + tuning.cat.height * 0.85, this.position.z);
  }

  /** Current horizontal speed. */
  get speed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  /** Reads the just-stepped dynamic pose before camera and perception sample it. */
  syncAfterPhysicsStep(): void {
    if (this.ragdoll.active) this.syncFromRagdoll();
  }

  /**
   * One fixed simulation step.
   * `basis` comes from the camera so movement is camera-relative.
   */
  step(dt: number, input: InputFrame, basis: { forward: Vec3; right: Vec3 }): void {
    this.landedThisStep = false;
    this.landedHardThisStep = false;
    this.landingImpactSpeed = 0;
    const cat = tuning.cat;

    this.dashCooldown = Math.max(0, this.dashCooldown - dt);

    if (this.locked) {
      this.noise = { loudness: 0, tag: 'silence' };
      return;
    }

    if (this.ragdoll.active) {
      this.ragdoll.advance(dt);
      this.syncFromRagdoll();
      this.state = 'tumbling';
      this.grounded = false;
      this.crouching = false;
      this.noise = { loudness: cat.ragdoll.noise, tag: 'tumble' };
      if (this.ragdoll.shouldRecover()) this.recoverFromRagdoll();
      return;
    }

    this.crouching = input.down.has('crouch') && this.grounded;

    // Desired horizontal direction, camera-relative.
    const wish = vec3(
      basis.forward.x * input.moveZ + basis.right.x * input.moveX,
      0,
      basis.forward.z * input.moveZ + basis.right.z * input.moveX,
    );
    const wishLength = lengthOf(wish);
    const wishDir = wishLength > 0.001 ? normalize(wish) : vec3();

    // --- Dash -------------------------------------------------------------
    if (input.justPressed.has('dash') && this.dashCooldown <= 0 && !this.crouching) {
      this.dashTimer = cat.dash.duration;
      this.dashCooldown = cat.dash.cooldown;
      // Dash in the direction of input, or straight ahead if standing still.
      this.dashDirection =
        wishLength > 0.001 ? wishDir : vec3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    }

    if (this.dashTimer > 0) {
      this.dashTimer -= dt;
      this.velocity.x = this.dashDirection.x * cat.dash.speed;
      this.velocity.z = this.dashDirection.z * cat.dash.speed;
      this.velocity.y += tuning.sim.gravity * dt * 0.35; // Floaty during the dash.
      this.state = 'dashing';
      this.faceDirection(this.dashDirection, dt);
      this.applyMovement(dt);
      this.noise = { loudness: cat.dash.noise, tag: 'dash' };
      return;
    }

    // --- Pounce -----------------------------------------------------------
    if (input.justPressed.has('pounce') && this.grounded) {
      this.pounceCharging = true;
      this.pounceCharge = 0;
    }
    if (this.pounceCharging) {
      if (input.down.has('pounce')) {
        this.pounceCharge = Math.min(this.pounceCharge + dt, cat.pounce.maxChargeTime);
      } else {
        this.releasePounce(wishDir, wishLength);
      }
    }

    // --- Jump -------------------------------------------------------------
    const canJump = this.grounded || this.coyoteTimer > 0;
    if (input.justPressed.has('jump') && canJump) {
      this.velocity.y = cat.jumpSpeed;
      this.coyoteTimer = 0;
      this.grounded = false;
    }

    // --- Horizontal movement ---------------------------------------------
    const targetSpeed = this.crouching
      ? cat.crouchSpeed
      : // Full input deflection means run. Partial deflection (a gamepad stick,
        // once implemented) scales down toward walk speed.
        wishLength >= 0.95
        ? cat.runSpeed
        : cat.walkSpeed * wishLength;

    const control = this.grounded ? 1 : cat.airControl;
    const desiredX = wishDir.x * targetSpeed;
    const desiredZ = wishDir.z * targetSpeed;

    const rate = (wishLength > 0.001 ? cat.acceleration : cat.deceleration) * control;
    this.velocity.x = approach(this.velocity.x, desiredX, rate * dt);
    this.velocity.z = approach(this.velocity.z, desiredZ, rate * dt);
    this.velocity.y += tuning.sim.gravity * dt;

    if (wishLength > 0.001) this.faceDirection(wishDir, dt);

    this.applyMovement(dt);

    // --- State + noise ----------------------------------------------------
    if (this.pounceCharging) this.state = 'grounded';
    else if (!this.grounded) this.state = 'airborne';
    else this.state = 'grounded';

    this.noise = this.computeNoise();

    if (this.grounded) this.coyoteTimer = cat.coyoteTime;
    else this.coyoteTimer = Math.max(0, this.coyoteTimer - dt);
  }

  private releasePounce(wishDir: Vec3, wishLength: number): void {
    const p = tuning.cat.pounce;
    this.pounceCharging = false;
    if (this.pounceCharge < p.minChargeTime) return; // A tap is not a pounce.

    const ratio = clamp(
      (this.pounceCharge - p.minChargeTime) / (p.maxChargeTime - p.minChargeTime),
      0,
      1,
    );
    const power = p.minSpeed + (p.maxSpeed - p.minSpeed) * ratio;
    const direction =
      wishLength > 0.001 ? wishDir : vec3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));

    this.velocity.x = direction.x * power * (1 - p.upwardRatio);
    this.velocity.z = direction.z * power * (1 - p.upwardRatio);
    this.velocity.y = power * p.upwardRatio;
    this.grounded = false;
    this.state = 'pouncing';
    this.noise = { loudness: p.noise, tag: 'pounce' };
  }

  /** Runs the movement through Rapier and writes back the resolved position. */
  private applyMovement(dt: number): void {
    const wasGrounded = this.grounded;
    const fallSpeed = -this.velocity.y;

    const desired = {
      x: this.velocity.x * dt,
      y: this.velocity.y * dt,
      z: this.velocity.z * dt,
    };

    this.controller.computeColliderMovement(this.collider, desired);
    const corrected = this.controller.computedMovement();
    this.grounded = this.controller.computedGrounded();

    const translation = this.body.translation();
    const next = {
      x: translation.x + corrected.x,
      y: translation.y + corrected.y,
      z: translation.z + corrected.z,
    };
    this.body.setNextKinematicTranslation(next);

    // The body is centered on the capsule; `position` is the cat's feet.
    this.position.x = next.x;
    this.position.y = next.y - tuning.cat.height / 2;
    this.position.z = next.z;

    // Zero out velocity along axes Rapier refused to move us, otherwise we keep
    // accumulating speed into a wall and shoot off when it finally clears.
    if (Math.abs(corrected.y) < Math.abs(desired.y) * 0.5) this.velocity.y = 0;
    if (this.grounded && this.velocity.y < 0) this.velocity.y = 0;

    if (!wasGrounded && this.grounded) {
      // Three bands: a settle onto the floor is silent, a real drop thumps, a big
      // fall knocks the cat over.
      this.landedThisStep = fallSpeed > tuning.cat.noise.landMinSpeed;
      if (fallSpeed > tuning.cat.ragdoll.fallSpeedThreshold) {
        this.landedHardThisStep = true;
        this.landingImpactSpeed = fallSpeed;
      }
    }
  }

  private faceDirection(direction: Vec3, dt: number): void {
    const target = Math.atan2(-direction.x, -direction.z);
    this.yaw = rotateToward(this.yaw, target, tuning.cat.turnRateDeg * DEG_TO_RAD * dt);
  }

  private computeNoise(): CatNoiseOutput {
    const n = tuning.cat.noise;
    if (this.landedHardThisStep) return { loudness: n.landHard, tag: 'land_hard' };
    // An ordinary touchdown still thumps. Without this a jump would be exactly as
    // quiet as a walking step, which makes hopping strictly better than walking.
    if (this.landedThisStep) return { loudness: n.land, tag: 'land' };
    if (!this.grounded) return { loudness: n.idle, tag: 'silence' };

    const speed = this.speed;
    if (speed < 0.15) return { loudness: n.idle, tag: 'silence' };
    if (this.crouching) return { loudness: n.crouch, tag: 'paw_soft' };
    if (speed > tuning.cat.walkSpeed * 1.2) return { loudness: n.run, tag: 'paw_run' };
    return { loudness: n.walk, tag: 'paw_walk' };
  }

  /** Switches from the kinematic controller to the articulated impact body. */
  startRagdoll(_reason: RagdollReason, impulse: Vec3): void {
    if (this.locked) return;
    if (!this.ragdoll.active) {
      this.body.setEnabled(false);
      this.ragdoll.activate(this.position, this.yaw, this.velocity, impulse);
    } else {
      this.ragdoll.addImpact(impulse);
    }
    this.crouching = false;
    this.pounceCharging = false;
    this.dashTimer = 0;
    this.grounded = false;
    this.state = 'tumbling';
  }

  /** Freezes input while the catch cutaway plays. */
  lock(): void {
    if (this.ragdoll.active) {
      this.syncFromRagdoll();
      this.restoreControllerAt(this.position);
    }
    this.locked = true;
    this.state = 'caught';
    this.velocity.x = 0;
    this.velocity.z = 0;
    // A cat in someone's arms is not a physical object in the world. Leaving the
    // collider live meant it sat inside granny's own capsule while she carried
    // it, and her character controller - which is exactly the thing that refuses
    // to walk into other colliders - would not let her take a single step.
    this.body.setEnabled(false);
  }

  unlock(): void {
    this.locked = false;
    this.state = 'grounded';
    this.body.setEnabled(true);
  }

  /**
   * Dangles a locked cat from a carry point, e.g. granny's hand on the way back
   * to the bed.
   *
   * Locked-only by design: this bypasses the controller entirely, and letting it
   * move a cat the player still controls would be a teleport with no collision
   * checks. `lock()` has already disabled the body, so this only moves the
   * visual. The gait solver keeps running, so the legs reach for a floor that is
   * no longer there - which is exactly how a scruffed cat hangs.
   */
  holdAt(position: Vec3, yaw: number): void {
    if (!this.locked) return;
    this.position.x = position.x;
    this.position.y = position.y;
    this.position.z = position.z;
    this.yaw = yaw;
  }

  /** Teleports the cat, e.g. back to its bed after being caught. */
  teleport(to: Vec3): void {
    this.position.x = to.x;
    this.position.y = to.y;
    this.position.z = to.z;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.velocity.z = 0;
    this.dashTimer = 0;
    this.pounceCharging = false;
    this.recoveryBlend = null;
    this.restoreControllerAt(to);
  }

  private syncFromRagdoll(): void {
    const pose = this.ragdoll.visualPose();
    this.position.x = pose.objectPosition.x;
    this.position.y = pose.objectPosition.y;
    this.position.z = pose.objectPosition.z;
    const velocity = this.ragdoll.linearVelocity;
    this.velocity.x = velocity.x;
    this.velocity.y = velocity.y;
    this.velocity.z = velocity.z;
  }

  private recoverFromRagdoll(): void {
    const pose = this.ragdoll.visualPose();
    const recovered = this.ragdoll.recoveryPosition(this.position.y);
    this.recoveryBlend = {
      position: new THREE.Vector3(
        pose.objectPosition.x,
        pose.objectPosition.y,
        pose.objectPosition.z,
      ),
      rotation: new THREE.Quaternion(
        pose.objectRotation.x,
        pose.objectRotation.y,
        pose.objectRotation.z,
        pose.objectRotation.w,
      ),
      elapsed: 0,
    };
    this.restoreControllerAt(recovered);
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.velocity.z = 0;
    this.grounded = true;
    this.state = 'grounded';
  }

  private restoreControllerAt(to: Vec3): void {
    this.ragdoll.deactivate();
    this.body.setEnabled(true);
    this.body.setTranslation({ x: to.x, y: to.y + tuning.cat.height / 2, z: to.z }, true);
    this.position.x = to.x;
    this.position.y = to.y;
    this.position.z = to.z;
  }
}

/** Moves `current` toward `target` by at most `maxDelta`. */
function approach(current: number, target: number, maxDelta: number): number {
  const delta = target - current;
  if (Math.abs(delta) <= maxDelta) return target;
  return current + Math.sign(delta) * maxDelta;
}

function buildPlaceholderRig(
  body: THREE.Object3D,
  skeleton: SkeletonDescription,
): { root: THREE.Object3D; bindings: ProceduralRigBindings } {
  const visual = tuning.cat.locomotion.skeleton;
  const bodyColor = getAssetDef('char.cat')?.placeholder.color ?? tuning.render.outlineColor;
  const fur = makeToonMaterial(bodyColor);
  const eyes = makeToonMaterial('#9ee7ff');
  const root = new THREE.Object3D();
  root.name = 'cat:procedural-rig';

  const spine = new THREE.Object3D();
  spine.name = 'cat:spine';
  spine.position.fromArray(visual.spinePivot);
  // Squashed and lifted, otherwise the torso lozenge covers the legs entirely and
  // the whole gait solver is invisible. See the skeleton $comment in tuning.json.
  body.scale.y = visual.bodyHeightScale;
  body.position.set(
    -visual.spinePivot[0],
    visual.bodyLift - visual.spinePivot[1],
    -visual.spinePivot[2],
  );
  spine.add(body);
  root.add(spine);

  const head = new THREE.Object3D();
  head.name = 'cat:head';
  head.position.set(
    visual.headOffset[0] - visual.spinePivot[0],
    visual.headOffset[1] - visual.spinePivot[1],
    visual.headOffset[2] - visual.spinePivot[2],
  );
  const headMesh = new THREE.Mesh(new THREE.SphereGeometry(visual.headRadius, 10, 8), fur);
  // Only the silhouette parts get outlined. Every shell is a second draw call, so
  // eyes, leg segments and paws stay bare - they are interior detail either way.
  addOutline(headMesh, { outlineClass: 'character' });
  head.add(headMesh);
  spine.add(head);

  const ears: [THREE.Object3D, THREE.Object3D] = [
    buildEar(visual.earOffsets[0], visual.earRadius, visual.earHeight, fur),
    buildEar(visual.earOffsets[1], visual.earRadius, visual.earHeight, fur),
  ];
  head.add(...ears);

  for (const offset of visual.eyeOffsets) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(visual.eyeRadius, 8, 6), eyes);
    eye.position.fromArray(offset);
    head.add(eye);
  }

  const segmentGeometry = new THREE.CylinderGeometry(
    visual.legThickness,
    visual.legThickness,
    skeleton.legLength / 2,
    6,
  );
  segmentGeometry.translate(0, -skeleton.legLength / 4, 0);
  const pawGeometry = new THREE.SphereGeometry(visual.pawRadius, 6, 4);
  const legs: Record<LegName, LegRigBinding> = {
    frontLeft: buildLeg('frontLeft', root, segmentGeometry, pawGeometry, fur),
    frontRight: buildLeg('frontRight', root, segmentGeometry, pawGeometry, fur),
    hindLeft: buildLeg('hindLeft', root, segmentGeometry, pawGeometry, fur),
    hindRight: buildLeg('hindRight', root, segmentGeometry, pawGeometry, fur),
  };

  const tail: THREE.Object3D[] = [];
  const tailGeometry = new THREE.CylinderGeometry(
    visual.tailThickness,
    visual.tailThickness,
    visual.tailJointLength,
    6,
  );
  tailGeometry.rotateX(Math.PI / 2);
  tailGeometry.translate(0, 0, visual.tailJointLength / 2);
  let tailParent = spine;
  for (let index = 0; index < skeleton.tailJointCount; index++) {
    const joint = new THREE.Object3D();
    joint.name = `cat:tail-${index}`;
    if (index === 0) {
      joint.position.set(
        visual.tailBaseOffset[0] - visual.spinePivot[0],
        visual.tailBaseOffset[1] - visual.spinePivot[1],
        visual.tailBaseOffset[2] - visual.spinePivot[2],
      );
      joint.rotation.x = visual.tailLift;
    } else {
      joint.position.z = visual.tailJointLength;
    }
    const mesh = new THREE.Mesh(tailGeometry, fur);
    addOutline(mesh, { outlineClass: 'character' });
    joint.add(mesh);
    tailParent.add(joint);
    tail.push(joint);
    tailParent = joint;
  }

  return { root, bindings: { root, spine, head, legs, tail, ears } };
}

function buildLeg(
  name: LegName,
  root: THREE.Object3D,
  segmentGeometry: THREE.BufferGeometry,
  pawGeometry: THREE.BufferGeometry,
  material: THREE.Material,
): LegRigBinding {
  const upper = new THREE.Object3D();
  const lower = new THREE.Object3D();
  const paw = new THREE.Object3D();
  upper.name = `cat:${name}-upper`;
  lower.name = `cat:${name}-lower`;
  paw.name = `cat:${name}-paw`;

  const upperMesh = new THREE.Mesh(segmentGeometry, material);
  const lowerMesh = new THREE.Mesh(segmentGeometry, material);
  const pawMesh = new THREE.Mesh(pawGeometry, material);
  upper.add(upperMesh);
  lower.add(lowerMesh);
  paw.add(pawMesh);
  root.add(upper, lower, paw);
  return { upper, lower, paw };
}

function buildEar(
  offset: [number, number, number],
  radius: number,
  height: number,
  material: THREE.Material,
): THREE.Object3D {
  const joint = new THREE.Object3D();
  joint.position.fromArray(offset);
  const mesh = new THREE.Mesh(new THREE.ConeGeometry(radius, height, 6), material);
  mesh.position.y = height / 2;
  addOutline(mesh, { outlineClass: 'character' });
  joint.add(mesh);
  return joint;
}
